import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { RefreshCw } from 'lucide-react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { DataTable } from '@duke-hq/ui/components/DataTable/index';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { SortOrder } from '@duke-hq/lib/pagination';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { DbStatsMetrics } from './DbStatsMetrics';

export function DbStatsPanelPage() {
  const { panelId } = useParams();
  const [sortOrder, setSortOrder] = useState<Record<string, SortOrder>>({});

  // Sorting applies to the rows of the panel you are on; reset it when the panel changes.
  useEffect(() => {
    setSortOrder({});
  }, [panelId]);

  const { data, isFetching, isError, error, refetch } = trpcAdmin.db.run.useQuery(
    { panel: panelId ?? '', sortOrder },
    { enabled: typeof panelId === 'string', refetchOnWindowFocus: false },
  );

  const onSortChange = useCallback(
    (columnName: string, direction?: SortOrder) =>
      setSortOrder((previous) => {
        if (direction === undefined) {
          const { [columnName]: _removed, ...rest } = previous;
          return rest;
        }
        return { ...previous, [columnName]: direction };
      }),
    [],
  );

  if (typeof panelId !== 'string') {
    return <DbStatsEmptyState />;
  }

  return (
    <Stack gap="0" h="full" w="full" bg="bg.surface">
      <Box p="4">
        <HStack justify="space-between" align="flex-start" gap="4">
          <Stack gap="2">
            <PageHeading>{data?.title ?? panelId}</PageHeading>
            {data?.description !== undefined ? (
              <Text color="fg.muted" fontSize="sm" maxW="prose">
                {data.description}
              </Text>
            ) : null}
          </Stack>
          <Button
            onClick={() => {
              void refetch();
            }}
            isLoading={isFetching}
            leftIcon={<Icon as={RefreshCw} boxSize={iconSize.sm} />}
            size="sm"
            variant="outline"
            colorScheme="purple"
            flexShrink={0}
          >
            <Trans>Refresh</Trans>
          </Button>
        </HStack>
        {data?.ranAt !== undefined ? (
          <Text color="fg.subtle" fontSize="xs" mt="2">
            <Trans>Ran at {data.ranAt.toLocaleTimeString()}</Trans>
          </Text>
        ) : null}
      </Box>

      <Box px="4" pb="4">
        {isError ? (
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        ) : data === undefined ? (
          <Center py="10">
            <Spinner color="purple.500" size="lg" />
          </Center>
        ) : data.view === 'notice' ? (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>{data.note}</AlertDescription>
          </Alert>
        ) : data.view === 'metrics' ? (
          <DbStatsMetrics metrics={data.metrics} />
        ) : data.table.rows.length === 0 ? (
          <Text color="fg.muted" fontSize="sm">
            <Trans>No rows returned.</Trans>
          </Text>
        ) : (
          <DataTable
            data={data.table}
            onSortChange={onSortChange}
            compact
            tableProps={{
              size: 'sm',
              colorScheme: 'purple',
              sx: {
                'thead th, tbody td': {
                  borderRightWidth: '1px',
                  borderColor: 'border.default',
                },
                'thead th:last-of-type, tbody td:last-of-type': {
                  borderRightWidth: 0,
                },
              },
            }}
          />
        )}
      </Box>
    </Stack>
  );
}

function DbStatsEmptyState() {
  return (
    <Center h="full" p="8">
      <Text color="fg.muted" fontSize="sm">
        <Trans>Select a query from the sidebar to run it.</Trans>
      </Text>
    </Center>
  );
}
